import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function EnvoyerMessageGlobal() {
    const [message, setMessage] = useState('');
    const [type, setType] = useState('info');
    const [destinataires, setDestinataires] = useState(['etudiant', 'enseignant', 'pat', 'rh', 'administrateur']);
    const [sending, setSending] = useState(false);
    const [preview, setPreview] = useState(false);

    // Utilisation de la configuration centralisée
    const API_BASE = `${config.apiBase}/technicien`;

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!message.trim()) {
            toast.error('Message requis');
            return;
        }

        setSending(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/envoyer_message_global.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    message: message,
                    type: type,
                    destinataires: destinataires
                })
            });
            const data = await response.json();
            
            if (data.success) {
                toast.success(`Message envoyé à ${data.total_envoyes} utilisateurs`);
                setMessage('');
                setPreview(false);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur lors de l\'envoi');
        } finally {
            setSending(false);
        }
    };

    const roles = [
        { value: 'etudiant', label: '👨‍🎓 Étudiants', color: 'blue' },
        { value: 'enseignant', label: '👨‍🏫 Enseignants', color: 'green' },
        { value: 'pat', label: '🍽️ Personnel PAT', color: 'orange' },
        { value: 'rh', label: '📋 RH', color: 'purple' },
        { value: 'administrateur', label: '👑 Administrateurs', color: 'red' },
        { value: 'technicien', label: '🔧 Techniciens', color: 'gray' }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="mx-auto max-w-4xl">
                {/* Header */}
                <div className="mb-8 rounded-2xl bg-gradient-to-r from-teal-600 to-cyan-600 p-6 text-white shadow-xl">
                    <h1 className="text-2xl font-bold">📢 Envoi de message global</h1>
                    <p className="mt-2 opacity-90">
                        Envoyez des communications techniques à tous les utilisateurs ou à des rôles spécifiques
                    </p>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                    {/* Formulaire */}
                    <div className="rounded-xl bg-white p-6 shadow-lg">
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="mb-2 block text-sm font-semibold text-slate-700">
                                    Type de message
                                </label>
                                <div className="flex gap-3">
                                    {[
                                        { value: 'info', label: 'ℹ️ Info', color: 'blue' },
                                        { value: 'warning', label: '⚠️ Attention', color: 'yellow' },
                                        { value: 'urgent', label: '🔴 Urgent', color: 'red' },
                                        { value: 'success', label: '✅ Succès', color: 'green' }
                                    ].map(t => (
                                        <button
                                            key={t.value}
                                            type="button"
                                            onClick={() => setType(t.value)}
                                            className={`rounded-lg px-4 py-2 transition ${
                                                type === t.value
                                                    ? `bg-${t.color}-600 text-white`
                                                    : `bg-${t.color}-100 text-${t.color}-700`
                                            }`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="mb-2 block text-sm font-semibold text-slate-700">
                                    Destinataires
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setDestinataires(roles.map(r => r.value))}
                                        className="rounded-lg bg-slate-100 px-3 py-1 text-sm transition hover:bg-slate-200"
                                    >
                                        Tous
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setDestinataires([])}
                                        className="rounded-lg bg-slate-100 px-3 py-1 text-sm transition hover:bg-slate-200"
                                    >
                                        Aucun
                                    </button>
                                    {roles.map(role => (
                                        <label key={role.value} className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-slate-100 px-3 py-1 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={destinataires.includes(role.value)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setDestinataires([...destinataires, role.value]);
                                                    } else {
                                                        setDestinataires(destinataires.filter(r => r !== role.value));
                                                    }
                                                }}
                                                className="rounded"
                                            />
                                            {role.label}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="mb-2 block text-sm font-semibold text-slate-700">
                                    Message
                                </label>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    rows={6}
                                    placeholder="Écrivez votre message technique ici..."
                                    className="w-full rounded-xl border border-slate-200 p-3 outline-none focus:border-teal-400"
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setPreview(!preview)}
                                    className="rounded-lg bg-slate-200 px-6 py-2 font-semibold text-slate-700 transition hover:bg-slate-300"
                                >
                                    {preview ? 'Masquer' : 'Aperçu'}
                                </button>
                                <button
                                    type="submit"
                                    disabled={sending || !message.trim() || destinataires.length === 0}
                                    className="flex-1 rounded-lg bg-gradient-to-r from-teal-600 to-cyan-600 px-6 py-2 font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                                >
                                    {sending ? 'Envoi...' : '📤 Envoyer'}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Aperçu */}
                    {preview && (
                        <div className="rounded-xl bg-white p-6 shadow-lg">
                            <h3 className="mb-4 text-lg font-semibold">📱 Aperçu du message</h3>
                            <div className={`rounded-lg p-4 ${
                                type === 'info' ? 'bg-blue-50 text-blue-800' :
                                type === 'warning' ? 'bg-yellow-50 text-yellow-800' :
                                type === 'urgent' ? 'bg-red-50 text-red-800' :
                                'bg-green-50 text-green-800'
                            }`}>
                                <div className="mb-2 flex items-center gap-2">
                                    <span className="font-bold">
                                        {type === 'info' ? 'ℹ️ Information' :
                                         type === 'warning' ? '⚠️ Attention' :
                                         type === 'urgent' ? '🔴 Urgent' :
                                         '✅ Notification'}
                                    </span>
                                    <span className="text-xs opacity-75">
                                        Technique
                                    </span>
                                </div>
                                <p className="whitespace-pre-wrap">{message || 'Message vide'}</p>
                                <div className="mt-3 text-xs opacity-50">
                                    Envoyé à : {destinataires.map(r => {
                                        const role = roles.find(ro => ro.value === r);
                                        return role?.label.split(' ')[0];
                                    }).join(', ')}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}